import styled from '@emotion/styled';
import { Variables } from '../../base';

interface ITabProps {
    active?: boolean;
}

export const Container = styled.div`
    border: 1px solid ${Variables.Colors.n15};
    border-radius: ${Variables.Rounded.large};
    box-shadow: ${Variables.Shadows.small};
    padding: 21px;
    margin: 12px 24px; 
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const TabsContainer = styled.div`
    display: flex;
    border-bottom: 1px solid ${Variables.Colors.n15};
`;

export const Tab = styled.button<ITabProps>`
    flex: 1;
    padding: 12px 16px;
    border: none;
    background-color: ${props => props.active ? Variables.Colors.white : Variables.Colors.n5};
    color: ${props => props.active ? Variables.Colors.b300 : Variables.Colors.n70};
    font-size: 15px;
    font-weight: ${props => props.active ? '600' : '500'};
    cursor: pointer;
    border-bottom: ${props => props.active ? `2px solid ${Variables.Colors.b300}` : 'none'};
    transition: all 0.2s ease;
    
    &:hover {
        background-color: ${props => props.active ? Variables.Colors.white : Variables.Colors.n15_16};
    }
    
    &:first-of-type {
        border-top-left-radius: ${Variables.Rounded.small};
    }
    
    &:last-child {
        border-top-right-radius: ${Variables.Rounded.small};
    }
`;

export const TabContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: -4px;
`;

export const SectionTitle = styled.h4`
    margin: 0;
    padding: 0;
    font-size: 14px;
    font-weight: 700;
    color: #334155;
    line-height: 20px;
`;

export const InlineSummary = styled.span`
    margin-left: 4px;
    font-size: 14px;
    font-weight: 600;
    color: #3b82f6;
`;

export const ChevronButton = styled.button`
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    background-color: transparent;
    color: #64748b;
    cursor: pointer;
    border-radius: ${Variables.Rounded.small};
    transition: all 0.2s ease;
    
    &:hover {
        background-color: ${Variables.Colors.n15_16};
        color: #3b82f6;
    }
    
    &:active {
        background-color: ${Variables.Colors.n15_24};
    }
`;

export const SearchSection = styled.div``;

export const SummarySection = styled.div`
    display: flex;
    gap: 12px;
    justify-content: center;
    padding: 13px;
    background-color: ${Variables.Colors.white};
    border: 1px solid #e2e8f0;
    border-radius: ${Variables.Rounded.medium};
`;

export const SummaryLabel = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
`;

export const SummaryLabelTitle = styled.div`
    font-size: 12px;
    font-weight: ${Variables.FontWeight.regular};
    color: ${Variables.Colors.n70};
    text-align: center;
`;

export const SummaryLabelValue = styled.div`
    font-size: 20px;
    font-weight: ${Variables.FontWeight.semibold};
    color: ${Variables.Colors.n100};
    line-height: 28px;
    
    &.contract-value {
        color: ${Variables.Colors.b300};
    }
`;

export const LoadingMessage = styled.div`
    text-align: center;
    padding: 32px;
    color: ${Variables.Colors.n70};
    font-size: 14px;
`;

export const EmptyMessage = styled.div`
    text-align: center;
    padding: 32px;
    color: ${Variables.Colors.n50};
    font-size: 14px;
`;

export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    background-color: ${Variables.Colors.white};
    border: 1px solid #e2e8f0;
    border-radius: ${Variables.Rounded.medium};
`;

export const TableHead = styled.thead`
    background-color: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
`;

export const TableBody = styled.tbody`
    overflow: visible;
`;

export const TableRow = styled.tr`
    &:not(:last-child) {
        border-bottom: 1px solid #e2e8f0;
    }
    
    &:hover {
        background-color: ${Variables.Colors.n5};
        
        /* Show action buttons on row hover */
        .actions-container {
            opacity: 1;
        }
    }
`;

export const TableHeader = styled.th`
    padding: 12px;
    text-align: left;
    font-size: 12px;
    font-weight: ${Variables.FontWeight.semibold};
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    &.align-right {
        text-align: right;
        padding-right: 12px;
    }
`;

export const TableCell = styled.td`
    padding: 12px;
    font-size: 14px;
    color: #1e293b;
    
    &.patient-name {
        font-weight: ${Variables.FontWeight.medium};
    }
    
    &.dos {
        font-weight: ${Variables.FontWeight.regular};
        color: #475569;
    }
    
    &.value {
        font-weight: ${Variables.FontWeight.semibold};
        text-align: right;
    }
    
    &.actions {
        text-align: center;
        padding: 13.75px 12px;
        overflow: visible;
    }
`;

export const ActionsContainer = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    opacity: 0;
    transition: opacity 0.2s ease;
`;

export const ActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    border: none;
    background-color: transparent;
    color: ${Variables.Colors.n70};
    cursor: pointer;
    border-radius: ${Variables.Rounded.small};
    transition: all 0.2s ease;
    
    &:hover {
        background-color: ${Variables.Colors.n15_16};
        color: ${Variables.Colors.b300};
    }
    
    &:active {
        background-color: ${Variables.Colors.n15_24};
    }
`;

export const MenuContainer = styled.div`
    position: relative;
    display: inline-block;
`;

export const MenuButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    padding: 0;
    border: none;
    background-color: transparent;
    color: #94a3b8;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
        color: #475569;
    }
`;

export const DropdownMenu = styled.div`
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    min-width: 180px;
    background-color: ${Variables.Colors.white};
    border: 1px solid #e2e8f0;
    border-radius: ${Variables.Rounded.medium};
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    z-index: 1000;
    overflow: hidden;
`;

export const MenuItem = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 16px;
    border: none;
    background-color: transparent;
    color: #1e293b;
    font-size: 14px;
    font-weight: ${Variables.FontWeight.medium};
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s ease;
    
    &:hover {
        background-color: #f8fafc;
    }
    
    &:not(:last-child) {
        border-bottom: 1px solid #f1f5f9;
    }
    
    svg {
        color: #64748b;
        flex-shrink: 0;
    }
`;

export const TableFooter = styled.div`
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: flex-end;
`;

export const PaginationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

export const PaginationInfo = styled.div`
    font-size: 13px;
    color: ${Variables.Colors.n70};
`;

export const PaginationControls = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`;

export const PageInfo = styled.span`
    font-size: 14px;
    color: ${Variables.Colors.n85};
    font-weight: 500;
`;

export const CopyAllButton = styled.button`
    align-self: flex-end;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    color: ${Variables.Colors.b300};
    background-color: transparent;
    border: 1px solid ${Variables.Colors.b300};
    border-radius: ${Variables.Rounded.small};
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 32px;
    min-height: 32px;
    
    &:hover {
        background-color: ${Variables.Colors.b300_l96};
    }
    
    &:active {
        background-color: ${Variables.Colors.b300_l90};
    }
`;

export const BulkSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const BulkLabel = styled.label`
    font-size: 14px;
    font-weight: 500;
    color: ${Variables.Colors.n85};
`;

export const BulkError = styled.div`
    color: ${Variables.Colors.r50};
    font-size: 14px;
    margin-top: 8px;
`;

export const BulkButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
`;

